import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ExternalLink } from 'lucide-react';
import styled from 'styled-components';
import { Button, CardTitle, HintNote, Message } from '../admin/ui';
import StudyClaimsList from '../publicProfile/StudyClaimsList';
import * as Api from './PersonAdminApi';
import { errorText } from './personAdminModel';
import { formatMoment } from './personAdminParts';

const Row = styled.div`display:flex;flex-wrap:wrap;gap:.75rem;align-items:center;margin-bottom:.75rem;font-size:.9rem;`;
const PageLink = styled.a`display:inline-flex;align-items:center;gap:.3rem;color:#0369a1;font-weight:600;text-decoration:none;&:hover{text-decoration:underline;}`;
const Studies = styled.div`margin-top:1.1rem;`;
const StudiesTitle = styled.h3`font-size:.95rem;margin:0 0 .5rem;`;

// The person's public profile on the admin's person page (design study-feed §5.1, §6.2): the link to
// the page, whether it is shown, the switch that only admins have (S11), and the review of the
// Austrian studies HEARTPORT EXPERT found by the person's name - the same list the person sees on
// their own profile, here with who decided. A profile exists only for a person named in a published
// program or in an event body; the page answers "not found" for anyone else.
const PublicProfileCard = ({ personId }) => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => Api.fetchPublicProfile(personId)
    .then((v) => { setView(v); setError(null); })
    .catch((e) => setError(errorText(e, t('personAdmin.publicProfile.loadFailed', 'The public profile could not be read.')))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personId]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async () => {
    setBusy(true);
    try {
      await Api.setPublicProfileHidden(personId, !view.hidden);
      await load();
    } catch (e) {
      setError(errorText(e, t('personAdmin.publicProfile.saveFailed', 'The change could not be saved.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CardTitle><FormattedMessage id="personAdmin.publicProfile.title" defaultMessage="Public profile" /></CardTitle>
      {error && <Message error role="alert">{error}</Message>}
      {view && (
        <>
          <Row>
            <PageLink href={`/people/${personId}`} target="_blank" rel="noreferrer">
              {t('personAdmin.publicProfile.open', 'Open the public profile')}
              <ExternalLink size={14} aria-hidden="true" />
            </PageLink>
            <span>
              {view.hidden
                ? t('personAdmin.publicProfile.hidden', 'Hidden by {name} on {date}', {
                  name: view.hiddenBy || t('personAdmin.publicProfile.anAdmin', 'an admin'),
                  date: view.hiddenAt ? formatMoment(intl, view.hiddenAt) : '–',
                })
                : t('personAdmin.publicProfile.shown', 'Shown')}
            </span>
            <Button type="button" secondary={!view.hidden} disabled={busy} onClick={toggleHidden}>
              {view.hidden
                ? t('personAdmin.publicProfile.show', 'Show the profile')
                : t('personAdmin.publicProfile.hide', 'Hide the profile')}
            </Button>
          </Row>
          <HintNote>
            {t('personAdmin.publicProfile.hint', 'Everyone named in a published program and every member of an event body has a public profile. Only the organizers can hide one; the person cannot.')}
          </HintNote>
          <Studies>
            <StudiesTitle>{t('personAdmin.publicProfile.studies', 'Co-authored Austrian studies')}</StudiesTitle>
            <StudyClaimsList
              admin
              claims={view}
              onDecide={(pmid, decision) => Api.decidePublicProfileStudy(personId, pmid, decision).then(load)}
            />
          </Studies>
        </>
      )}
    </>
  );
};

export default PublicProfileCard;
